/**
 * The typeface a label prints in.
 *
 * Bundled rather than fetched. Labels get printed at a card show, off a phone,
 * on whatever network the venue has - which is often none. A face that arrives
 * over the wire is a face that silently falls back to the system default
 * halfway through a run of two hundred stickers, and half the roll comes out in
 * a different font.
 *
 * Roboto Condensed, chosen for width. Width is the only dimension a 40mm strip
 * is short of, and a condensed face fits noticeably more of a card name before
 * the fitting loop has to shrink it. Only the latin subset ships - 45KB - since
 * the rest is megabytes for characters no card name uses.
 *
 * The weights are 600 and 700. Nothing lighter: a thermal head burns a dot or
 * it does not, so a light weight does not come out light, it comes out broken.
 */
import fontUrl from '../../assets/fonts/RobotoCondensed-latin.woff2';

export const LABEL_FONT = '"Roboto Condensed", "Helvetica Neue", Arial, sans-serif';

/** A canvas font string in the label's face. */
export const labelFace = (px, weight) => `${weight} ${px}px ${LABEL_FONT}`;

let ready = null;

/**
 * Load the face before anything measures text in it.
 *
 * Canvas silently substitutes when a font is not loaded yet, and it does it at
 * measure time as well as at draw time - so a label laid out before the font
 * arrives is laid out to the wrong widths and then drawn in the right face,
 * which is worse than either. Everything that renders a label awaits this.
 */
export function ensureLabelFont() {
  if (ready) return ready;
  ready = (async () => {
    if (typeof document === 'undefined' || !document.fonts) return;
    try {
      const face = new FontFace('Roboto Condensed', `url(${fontUrl})`, {
        weight: '100 900',
        style: 'normal',
      });
      await face.load();
      document.fonts.add(face);
      // Ask for the two weights by name as well: adding the FontFace is not the
      // same as the canvas being willing to use it yet.
      await Promise.all([
        document.fonts.load(labelFace(20, 600), 'Charizard'),
        document.fonts.load(labelFace(20, 700), '$1340'),
      ]);
    } catch (err) {
      // A missing face is a label in Helvetica, not a failed print.
      console.warn('[label] font unavailable, falling back:', err?.message);
    }
  })();
  return ready;
}
